import { useEffect, useState } from "react";

const HelpCenterDetail = ({ section, onBack, onVideoWatched, progress = {}, area = "help", onToggleRow }) => {
  const [activeMedia, setActiveMedia] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const showVideoThumbnail = (event, media) => {
    if (media.poster || media.thumbnail) return;

    const video = event.currentTarget;
    const previewTime = Number.isFinite(video.duration)
      ? Math.min(3, Math.max(video.duration / 3, 0.1))
      : 3;
    if (video.currentTime < 0.1) video.currentTime = previewTime;
  };

  useEffect(() => {
    if (!activeMedia) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActiveMedia(null);
      if (activeMedia.type !== "gallery") return;
      if (event.key === "ArrowRight") {
        setActiveImageIndex((current) =>
          (current + 1) % activeMedia.images.length
        );
      }
      if (event.key === "ArrowLeft") {
        setActiveImageIndex((current) =>
          (current - 1 + activeMedia.images.length) % activeMedia.images.length
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeMedia]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [section?.id]);

  if (!section) return <div>Not found</div>;

  const visibleContent = section.content || [];

  const firstExampleIndex = visibleContent.findIndex(
    (block) => block.type === "example"
  );

  const openMedia = (media) => {
    setActiveImageIndex(0);
    setActiveMedia(media);
  };

  const mediaPlaceholder = (label, large = false) => (
    <span className={`flex w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 font-semibold text-gray-500 ${
      large ? "h-[min(70vh,36rem)] rounded-lg text-lg" : "h-full text-sm"
    }`}>
      {label} placeholder
    </span>
  );

  const renderMedia = (media, mediaIndex, thumbnail = false, rowIndex = null) => (
    <figure key={mediaIndex} className="mx-auto w-full max-w-56">
      <button
        type="button"
        onClick={() => openMedia(media)}
        className={`group relative mx-auto block w-full max-w-56 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 ${
          thumbnail ? "h-28" : ""
        }`}
        aria-label={`Open ${media.type} in a larger view`}
      >
        {media.type === "image" && media.placeholder &&
          mediaPlaceholder("Image")}
        {media.type === "image" && !media.placeholder && (
          <img
            src={media.src}
            alt={media.alt || ""}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        )}
        {media.type === "video" && media.placeholder && (
          <>
            {mediaPlaceholder("Video")}
            <span className="absolute inset-0 flex items-center justify-center text-3xl text-gray-600">
              ▶
            </span>
          </>
        )}
        {media.type === "video" && !media.placeholder && (
          <>
            <video
              className="h-full w-full object-cover"
              preload="auto"
              muted
              playsInline
              poster={media.poster || media.thumbnail || undefined}
              onLoadedMetadata={(event) => showVideoThumbnail(event, media)}
            >
              <source src={media.src} type={media.mimeType || "video/mp4"} />
            </video>
            <span className="absolute inset-0 flex items-center justify-center bg-black/15 text-3xl text-white transition-colors group-hover:bg-black/25">
              ▶
            </span>
          </>
        )}
        {media.type === "video" && progress[`${area}:${section.id}-row-${rowIndex}-video`]?.videoWatched && (
          <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 font-bold text-white shadow" aria-label="Video watched">✓</span>
        )}
      </button>
      {media.caption && !thumbnail && (
        <figcaption className="mt-2 text-sm text-gray-600">
          {media.caption}
        </figcaption>
      )}
    </figure>
  );

  const renderMediaGroup = (media = [], rowIndex = null) => {
    const images = media.filter(
      (item) => item.type === "image" && (item.src || item.placeholder)
    );
    const otherMedia = media.filter(
      (item) => item.type !== "image" && (item.src || item.placeholder)
    );
    const displayMedia = images.length > 1
      ? [{
          type: "gallery",
          images,
          caption: images.find((image) => image.caption)?.caption,
        }, ...otherMedia]
      : [...images, ...otherMedia];

    return displayMedia.map((item, mediaIndex) => {
      if (item.type !== "gallery") return renderMedia({ ...item, rowIndex }, mediaIndex, true, rowIndex);

      return (
        <figure key={`gallery-${mediaIndex}`} className="mx-auto w-full max-w-56">
          <button
            type="button"
            onClick={() => openMedia(item)}
            className="group relative mx-auto block h-28 w-[calc(100%-0.75rem)]"
            aria-label={`Open gallery of ${item.images.length} images`}
          >
            <span className="absolute inset-0 translate-x-3 -rotate-2 rounded-lg border border-gray-200 bg-gray-200" />
            <span className="absolute inset-0 translate-x-1.5 rotate-1 rounded-lg border border-gray-200 bg-gray-100" />
            <span className="absolute inset-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              {item.images[0].placeholder ? (
                mediaPlaceholder("Images")
              ) : (
                <img
                  src={item.images[0].src}
                  alt={item.images[0].alt || ""}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                />
              )}
              <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white">
                1 / {item.images.length}
              </span>
            </span>
          </button>
        </figure>
      );
    });
  };

  return (
    <div className="mx-auto min-h-screen max-w-7xl px-4 pb-24 pt-4 sm:px-8 lg:px-12 lg:pb-12">
      <div className="sticky top-[83px] z-30 -mx-2 mb-6 border-b border-gray-100 bg-white px-2 pb-4 pt-2 shadow-sm">
        <button
          onClick={onBack}
          className="rounded-full bg-white px-3 py-2 font-medium text-blue-600 shadow-sm hover:underline"
        >
          ← Back
        </button>
        <h1 className="mt-3 text-2xl font-bold leading-tight sm:text-3xl">{section.title}</h1>
      </div>
      <div className="h-10 sm:h-12" aria-hidden="true" />

      {/* VIDEO */}
      {section.video && (
        <div className="relative mx-auto mb-6 flex max-w-2xl justify-center">
            <video
            className="w-full max-w-2xl rounded-lg"
            controls
            preload="metadata"
            onEnded={() => onVideoWatched?.({ itemId: `${section.id}-video`, title: `${section.title} video` })}
            >
            <source src={section.video} type="video/mp4" />
            Your browser does not support the video tag.
            </video>
            {progress[`${area}:${section.id}-video`]?.videoWatched && (
              <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow">✓ Watched</span>
            )}
        </div>
      )}

      {/* CONTENT */}
      <div>
        {visibleContent.map((block, index) => {
          switch (block.type) {
            case "header":
              return (
                <h2 key={index} className="mb-4 text-xl font-semibold">
                  {block.text}
                </h2>
              );
            case "text":
              return <p key={index} className="mb-4 whitespace-pre-line">
                      {block.text}
                     </p>;
            case "image":
              return (
                <img
                  key={index}
                  src={block.src}
                  alt=""
                  className="mb-4 rounded-lg"
                />
              );
            case "example":
              return (
                <div key={index}>
                  {index === firstExampleIndex && (
                    <div className="hidden grid-cols-[6.5rem_1.05fr_1.1fr_1.3fr_1.55fr] rounded-t-xl bg-gradient-to-r from-blue-500 to-blue-400 text-center text-sm font-semibold text-white lg:grid">
                      <div className="flex items-center justify-center px-1 py-4 text-center">Complete</div>
                      <div className="flex items-center justify-center p-4">Developmental need addressed</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Rapport or engagement activity</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Example clinician language</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Plutonic feature in practice</div>
                    </div>
                  )}
                  <article className="grid overflow-hidden border border-t-0 border-gray-200 bg-white first:border-t lg:grid-cols-[6.5rem_1.05fr_1.1fr_1.3fr_1.55fr]">
                    <div className="flex items-center justify-center border-b border-gray-200 p-4 lg:border-b-0">
                      <label className="flex items-center gap-2 text-sm font-semibold text-blue-700 lg:flex-col">
                        <input
                          type="checkbox"
                          className="h-6 w-6 accent-blue-600"
                          checked={Boolean(progress[`${area}:${section.id}-row-${index}`]?.completed)}
                          onChange={(event) => onToggleRow?.(index, block, event.target.checked)}
                          aria-label={`Mark row ${index + 1} complete`}
                        />
                        <span className="lg:hidden">Complete this row</span>
                      </label>
                    </div>
                    <div className="p-5 lg:flex lg:self-stretch lg:flex-col lg:items-start lg:justify-center">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Developmental need addressed</p>
                      <p className="font-semibold">{block.need}</p>
                    </div>
                    <div className="border-t border-gray-200 p-5 lg:flex lg:self-stretch lg:flex-col lg:items-start lg:justify-center lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Rapport or engagement activity</p>
                      <p>{block.activity}</p>
                    </div>
                    <div className="border-t border-gray-200 p-5 lg:flex lg:self-stretch lg:flex-col lg:items-start lg:justify-center lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Example clinician language</p>
                      <p className="italic text-gray-800">{block.clinicianLanguage}</p>
                    </div>
                    <div className="space-y-4 border-t border-gray-200 p-5 lg:flex lg:h-full lg:flex-col lg:items-center lg:justify-center lg:space-y-0 lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Plutonic feature in practice</p>
                      {(!block.media || block.media.length === 0) && (
                        <p>{block.featureInPractice}</p>
                      )}
                      {block.media?.length > 0 && (
                        <div className="flex w-full flex-wrap justify-center gap-3">
                          {renderMediaGroup(block.media, index)}
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              );
            default:
              return null;
          }
        })}
      </div>

      {activeMedia && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
          onClick={() => setActiveMedia(null)}
        >
          <div
            className="relative w-full max-w-5xl rounded-xl bg-white p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveMedia(null)}
              className="absolute -right-3 -top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl text-gray-700 shadow-lg"
              aria-label="Close media preview"
            >
              ×
            </button>
            {activeMedia.type === "gallery" ? (
              <div className="relative">
                {activeMedia.images[activeImageIndex].placeholder ? (
                  mediaPlaceholder(`Image ${activeImageIndex + 1}`, true)
                ) : (
                  <img
                    src={activeMedia.images[activeImageIndex].src}
                    alt={activeMedia.images[activeImageIndex].alt || ""}
                    className="max-h-[80vh] w-full rounded-lg object-contain"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setActiveImageIndex((current) =>
                    (current - 1 + activeMedia.images.length) % activeMedia.images.length
                  )}
                  className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-2xl text-white shadow-lg hover:bg-black/80"
                  aria-label="Previous image"
                >
                  &lsaquo;
                </button>
                <button
                  type="button"
                  onClick={() => setActiveImageIndex((current) =>
                    (current + 1) % activeMedia.images.length
                  )}
                  className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-2xl text-white shadow-lg hover:bg-black/80"
                  aria-label="Next image"
                >
                  &rsaquo;
                </button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-sm font-medium text-white">
                  {activeImageIndex + 1} / {activeMedia.images.length}
                </span>
              </div>
            ) : activeMedia.placeholder ? (
              mediaPlaceholder(
                activeMedia.type === "video" ? "Video" : "Image",
                true
              )
            ) : activeMedia.type === "image" ? (
              <img
                src={activeMedia.src}
                alt={activeMedia.alt || ""}
                className="max-h-[80vh] w-full rounded-lg object-contain"
              />
            ) : (
              <video
                className="max-h-[80vh] w-full rounded-lg bg-black"
                controls
                autoPlay
                onEnded={() => onVideoWatched?.({
                  itemId: `${section.id}-row-${activeMedia.rowIndex}-video`,
                  title: `${section.title} row ${activeMedia.rowIndex + 1} video`,
                })}
              >
                <source
                  src={activeMedia.src}
                  type={activeMedia.mimeType || "video/mp4"}
                />
                Your browser does not support the video tag.
              </video>
            )}
            {(activeMedia.type === "gallery"
              ? activeMedia.images[activeImageIndex].caption
              : activeMedia.caption) && (
              <p className="mt-3 text-sm text-gray-600">
                {activeMedia.type === "gallery"
                  ? activeMedia.images[activeImageIndex].caption
                  : activeMedia.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenterDetail;
